import React, { useMemo, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import { FaCamera } from 'react-icons/fa';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Text from '../Text';
import { dedupeFiles } from 'v3/utils/fileKey';
import { ContainerFile } from './styles';

function formatKb(bytes) {
  return `${Math.round((Number(bytes) || 0) / 1024)} KB`;
}

function UploadChecklistMedia({
  name,
  id,
  onChange,
  accept = '*',
  label,
  helperText,
  icon,
  error,
  multiple,
  files = [],
  onFilesChange,
  onRemove,
  disabled = false,
  hideFilesList = false,
}) {
  const inputRef = useRef(null);

  const filesArr = useMemo(() => Array.from(files || []), [files]);

  const fileLabel = useMemo(() => {
    if (!filesArr.length) return '';
    if (filesArr.length === 1) return filesArr[0]?.name || '';
    return `${filesArr.length} arquivos selecionados`;
  }, [filesArr]);

  function handleChange(event) {
    const selected = Array.from(event?.target?.files || []);
    const hasFileGreater = false;

    if (typeof onChange === 'function') {
      onChange(event, hasFileGreater);
    }

    // O mesmo arquivo (nome, tamanho e data de modificação) não entra duas vezes.
    const next = multiple ? dedupeFiles([...filesArr, ...selected]) : selected.slice(0, 1);
    const changed = !multiple || next.length !== filesArr.length;

    if (changed && typeof onFilesChange === 'function') {
      onFilesChange(next);
    }

    if (event?.target) event.target.value = '';
  }

  function handleRemove(index) {
    if (disabled) return;

    if (typeof onRemove === 'function') {
      onRemove(index);
      return;
    }

    if (typeof onFilesChange === 'function') {
      const next = [...filesArr];
      next.splice(index, 1);
      onFilesChange(next);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <ContainerFile
        display={12}
        onClick={() => {
          if (disabled) return;
          if (inputRef.current) inputRef.current.click();
        }}
        style={disabled ? { opacity: 0.65, cursor: 'not-allowed' } : null}
      >
        <Form.Control
          type="file"
          ref={inputRef}
          name={name}
          id={id}
          onChange={handleChange}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
        />

        {icon || <FaCamera size={30} color="#494950" className="icon" />}

        {error ? (
          <Text color="error" type="label">
            {error}
          </Text>
        ) : (
          <Text
            color="#494950"
            type="label"
            style={{
              width: '100%',
              maxWidth: '100%',
              textAlign: 'center',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {fileLabel}
          </Text>
        )}

        <Text color="#494950" type="label">
          {label}
        </Text>

        {helperText ? (
          <Text
            color="#6B7280"
            type="small"
            style={{ textAlign: 'center', lineHeight: 1.4 }}
          >
            {helperText}
          </Text>
        ) : null}
      </ContainerFile>

      {!hideFilesList && filesArr.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filesArr.map((f, i) => (
            <div
              key={`${f?.name || 'file'}-${f?.size || 0}-${i}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 10,
                border: '1px solid #E5E7EB',
                background: '#fff',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text
                  style={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                  }}
                  title={f?.name}
                >
                  {f?.name}
                </Text>
                <Text type="small" color="gray">
                  {formatKb(f?.size)}
                </Text>
              </div>

              <button
                type="button"
                onClick={() => handleRemove(i)}
                disabled={disabled}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: '#DC2626',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 4,
                }}
                title="Remover arquivo"
                aria-label={`Remover ${f?.name || 'arquivo'}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UploadChecklistMedia;
